import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import { FiArrowRight } from 'react-icons/fi';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/autoplay';
import './BlogSection.css';
import LoadingSpinner from './LoadingSpinner';
import TrueFocus from './reactbit/TrueFocus';

<TrueFocus 
sentence="True Focus"
manualMode={false}
blurAmount={5}
borderColor="red"
animationDuration={2}
pauseBetweenAnimations={1}
/>

const API_BASE_URL = (process.env.REACT_APP_API_URL).replace(/\/$/, '');

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const BlogSection = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Özet metnini 'content' alanından alıp belirli bir uzunlukta kesmek için
  const truncateSummary = (text) => {
    if (!text) return '';
    if (text.length <= 100) return text;
    return text.substring(0, 100) + '...';
  };

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const apiUrl = `${API_BASE_URL}/api/blogs`;
        const res = await fetch(apiUrl);
        if (!res.ok) throw new Error('API isteği başarısız');
        const data = await res.json();
        setPosts(Array.isArray(data) ? data : []);
      } catch (err) {
        setError('Blog yazıları yüklenirken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  return (
    <section className="blog-section">
      <div className="container">
        <div className="section-header blog-section-header">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.6 }}
          >
            <TrueFocus 
              sentence="Latest Blogs & News"
              manualMode={false}
              blurAmount={4}
              borderColor="#9281ffff"
              animationDuration={1}
              pauseBetweenAnimations={0.2}
            />
          </motion.div>
          <motion.p
            className="section-subtitle"
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 0.7, delay: 0.18, ease: "easeOut" }}
          >
            Explore SSI team’s latest insights and inspiring articles
          </motion.p>
        </div>
      </div>
      <motion.div
        variants={itemVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.4 }}
        className="blog-slider-container"
      >
        {loading && <LoadingSpinner text="Blog yazıları yükleniyor..." />}
        {error && <p style={{ textAlign: 'center', color: 'red' }}>{error}</p>}
        {!loading && !error && (
          <Swiper
            modules={[Navigation, Autoplay]}
            spaceBetween={30}
            slidesPerView={1}
            navigation={true}
            loop={true}
            autoplay={{
              delay: 2000,
              disableOnInteraction: false,
            }}
            breakpoints={{
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            className="blog-swiper"
          >
            {posts.map((post, idx) => (
              <SwiperSlide key={post.id}>
                <motion.div
                  className="blog-card"
                  initial={{ opacity: 0, y: 32 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: idx * 0.12 }}
                  viewport={{ once: true, amount: 0.3 }}
                >
                  <img src={post.imageUrl} alt={post.title} className="blog-card-image" />
                  <div className="blog-card-content">
                    <h3 className="blog-card-title">{post.title}</h3>
                    <p className="blog-card-excerpt">
                      {truncateSummary(post.content)}
                    </p>
                    <Link to={`/blogs/${post.slug}`} className="read-more-link">
                      Read More →
                    </Link>
                  </div>
                </motion.div>
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </motion.div>
      <div className="container">
        <motion.div
          className="all-posts-button-container"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          viewport={{ once: true, amount: 0.5 }}
        >
          <Link to="/blogs" className="all-posts-button">
            All Articles <FiArrowRight className="button-arrow-icon" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default BlogSection;